import {useCallback, useEffect, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {claveDeRecorte, pedirRecorte, type RecorteAlVuelo} from '@/hooks/bateria/use-recorte-al-vuelo'
import {cortesPorPrecargar, type TarjetaCola} from '@/lib/bateria-cola'

// Cada corte rectifica y recorta en el servidor (~65 ms): cuatro vacían la ventana de treinta en
// ~520 ms sin martillarlo con varios revisores a la vez.
const SIMULTANEOS = 4

/** Precalienta hacia adelante los cortes al vuelo en el mismo caché que lee la tarjeta; lo que sale de
 * la ventana no se pide. `calentarBytes` recibe cada URL firmada para bajar el bitmap antes de montarlo. */
export function usePrecargaCortes(
  businessId: string,
  evaluacionId: string,
  tarjetas: TarjetaCola[],
  indice: number,
  calentarBytes?: (url: string) => void
): void {
  const cliente = useQueryClient()
  const pendientes = useRef<{paqueteId: string; itemId: string}[]>([])
  const enVuelo = useRef(0)
  // Indirección por ref: el bombeo se reinvoca al liberar un cupo y un `useCallback` no puede nombrarse.
  const bombearRef = useRef<() => void>(() => {})

  const bombear = useCallback(() => {
    while (enVuelo.current < SIMULTANEOS && pendientes.current.length > 0) {
      const objetivo = pendientes.current.shift()
      if (objetivo === undefined) return
      enVuelo.current += 1
      const clave = claveDeRecorte(businessId, evaluacionId, objetivo.paqueteId, objetivo.itemId)
      const soltar = () => {
        enVuelo.current -= 1
        bombearRef.current()
      }
      // `prefetchQuery` no lanza: un 404 («fila sin corte») deja la consulta en error y la tarjeta
      // cae a la hoja.
      cliente
        .prefetchQuery({
          queryKey: clave,
          queryFn: () => pedirRecorte(businessId, evaluacionId, objetivo.paqueteId, objetivo.itemId),
          staleTime: Infinity,
          retry: false,
        })
        .then(() => {
          const datos = cliente.getQueryData<RecorteAlVuelo>(clave)
          if (datos?.url) calentarBytes?.(datos.url)
          soltar()
        }, soltar)
    }
  }, [cliente, businessId, evaluacionId, calentarBytes])

  useEffect(() => {
    bombearRef.current = bombear
  }, [bombear])

  useEffect(() => {
    pendientes.current = cortesPorPrecargar(tarjetas, indice)
    bombear()
  }, [tarjetas, indice, bombear])
}
